import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { isDesktopBreakpoint, mediaQueryLarge } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

/**
 * @typedef {object} QuickbuyVariant
 * @property {number} id
 * @property {string | null} sku
 * @property {boolean} available
 * @property {string[]} options
 * @property {number | null} mediaId
 * @property {number | null} [rMediaId]
 */

/**
 * @typedef {object} QuickbuyProductData
 * @property {number} id
 * @property {number | null} colorOptionPosition
 * @property {number | null} sizeOptionPosition
 * @property {QuickbuyVariant[]} variants
 */

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} [meta]
 * @property {HTMLElement} [colorName]
 * @property {HTMLElement} [sku]
 * @property {HTMLElement} [refLabel]
 * @property {HTMLElement} [colorGroup]
 * @property {HTMLElement} [sizeGroup]
 * @property {HTMLScriptElement} productData
 *
 * @extends {DialogComponent}
 */
export class SkreyQuickbuyComponent extends DialogComponent {
  requiredRefs = ['dialog', 'productData'];

  /** @type {QuickbuyProductData | null} */
  #data = null;

  /** @type {string} */
  #selectedColor = '';

  /** @type {string} */
  #selectedSize = '';

  /** @type {ReturnType<typeof setTimeout> | null} */
  #addedTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    this.#data = this.#parseProductData();
    this.#initSelectionFromAvailable();
    this.#syncUI();
    this.#bindAddToCartFeedback();
    this.#bindCarouselDragGuard();

    this.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#onDialogClose);
    mediaQueryLarge.addEventListener('change', this.#onBreakpointChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#onDialogClose);
    mediaQueryLarge.removeEventListener('change', this.#onBreakpointChange);
    this.#abortController.abort();

    if (this.#addedTimeout) clearTimeout(this.#addedTimeout);
  }

  /**
   * Mobile-only: open via DialogComponent (showModal + scroll lock + outside click).
   */
  showDialog() {
    if (isDesktopBreakpoint()) return;

    document.querySelectorAll('skrey-quickbuy-component').forEach((el) => {
      if (el !== this && el instanceof SkreyQuickbuyComponent && el.refs.dialog?.open) {
        el.closeDialog();
      }
    });

    super.showDialog();
  }

  /**
   * @param {Event} event
   */
  selectColor = (event) => {
    const button = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('[data-option-value]') : null
    );
    if (!button) return;

    event.stopPropagation();

    this.#selectedColor = button.dataset.optionValue || '';

    if (this.#selectedSize && !this.#isSizeAvailable(this.#selectedSize)) {
      this.#selectedSize = this.#firstAvailableSize() || this.#selectedSize;
    }

    this.#syncUI();
    this.#previewSelectedColorMedia(button.dataset.mediaId, button.dataset.rMediaId);
  };

  /**
   * @param {Event} event
   */
  selectSize = (event) => {
    const button = /** @type {HTMLButtonElement | null} */ (
      event.target instanceof Element ? event.target.closest('[data-option-value]') : null
    );
    if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

    event.stopPropagation();

    this.#selectedSize = button.dataset.optionValue || '';
    this.#syncUI();
  };

  /**
   * @returns {QuickbuyProductData | null}
   */
  #parseProductData() {
    try {
      return /** @type {QuickbuyProductData} */ (JSON.parse(this.refs.productData.textContent || '{}'));
    } catch {
      console.warn('[skrey-quickbuy] Failed to parse product data');
      return null;
    }
  }

  #initSelectionFromAvailable() {
    // Color: trust whatever Liquid already marked as selected — never override it.
    const selectedColorButton = this.refs.colorGroup?.querySelector('.is-selected[data-option-value]')
      ?? this.refs.colorGroup?.querySelector('[data-option-value]');

    this.#selectedColor = selectedColorButton instanceof HTMLElement
      ? selectedColorButton.dataset.optionValue || ''
      : '';

    // Size: prefer an available size for the selected color, falling back to
    // whatever Liquid marked as selected (which may be sold out).
    const selectedSizeButton = this.refs.sizeGroup?.querySelector('.is-selected[data-option-value]');
    const defaultSize = selectedSizeButton instanceof HTMLElement
      ? selectedSizeButton.dataset.optionValue || ''
      : '';

    const availableSize = this.#firstAvailableSize();

    this.#selectedSize = availableSize || defaultSize
      || (this.refs.sizeGroup?.querySelector('[data-option-value]') instanceof HTMLElement
        ? /** @type {HTMLElement} */ (this.refs.sizeGroup.querySelector('[data-option-value]')).dataset.optionValue || ''
        : '');
  }

  /**
   * @returns {string}
   */
  #firstAvailableSize() {
    const buttons = this.refs.sizeGroup?.querySelectorAll('[data-option-value]');
    if (!buttons) return '';

    for (const button of buttons) {
      if (!(button instanceof HTMLElement)) continue;
      const sizeValue = button.dataset.optionValue || '';
      if (this.#isSizeAvailable(sizeValue)) return sizeValue;
    }

    return '';
  }

  #syncUI() {
    this.#updateColorSelection();
    this.#updateSizeAvailability();
    this.#updateSizeSelection();

    const variant = this.#resolveVariant();
    this.#updateMeta(variant);
    this.#updateForm(variant);
  }

  #updateColorSelection() {
    const buttons = this.refs.colorGroup?.querySelectorAll('[data-option-value]');
    if (!buttons) return;

    for (const button of buttons) {
      if (!(button instanceof HTMLElement)) continue;
      const colorValue = button.dataset.optionValue || '';
      const selected = colorValue === this.#selectedColor;
      const available = this.#isColorAvailable(colorValue);

      button.classList.toggle('is-selected', selected);
      button.classList.toggle('is-disabled', !available);
      button.dataset.available = String(available);
      button.setAttribute('aria-checked', selected ? 'true' : 'false');
    }
  }

  #updateSizeSelection() {
    const buttons = this.refs.sizeGroup?.querySelectorAll('[data-option-value]');
    if (!buttons) return;

    for (const button of buttons) {
      if (!(button instanceof HTMLElement)) continue;
      const selected = button.dataset.optionValue === this.#selectedSize;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-checked', selected ? 'true' : 'false');
    }
  }

  #updateSizeAvailability() {
    const buttons = this.refs.sizeGroup?.querySelectorAll('[data-option-value]');
    if (!buttons || !this.#data) return;

    for (const button of buttons) {
      if (!(button instanceof HTMLButtonElement)) continue;

      const sizeValue = button.dataset.optionValue || '';
      const available = this.#isSizeAvailable(sizeValue);

      button.dataset.available = String(available);
      button.classList.toggle('is-disabled', !available);
      button.toggleAttribute('disabled', !available);
      button.setAttribute('aria-disabled', available ? 'false' : 'true');
    }
  }

  /**
   * @param {string} sizeValue
   * @returns {boolean}
   */
  #isSizeAvailable(sizeValue) {
    if (!this.#data) return false;

    return this.#data.variants.some((variant) => {
      if (!variant.available) return false;
      if (!this.#variantMatchesOptions(variant, this.#selectedColor, sizeValue)) return false;
      return true;
    });
  }

  /**
   * @param {string} colorValue
   * @returns {boolean}
   */
  #isColorAvailable(colorValue) {
    if (!this.#data || !colorValue) return false;

    const { colorOptionPosition } = this.#data;
    if (colorOptionPosition == null) return false;

    const colorIndex = colorOptionPosition - 1;

    return this.#data.variants.some(
      (variant) => variant.available && variant.options[colorIndex] === colorValue
    );
  }

  /**
   * Prefer the selected variant's media, then any media for the selected color.
   * Vista-3 / R-media views hide non-`_R_` slides with CSS, so those paths
   * must preview the still-life (`rMediaId`) or the image will not change.
   * @param {string | undefined} fallbackMediaId
   * @param {string | undefined} [fallbackRMediaId]
   */
  #previewSelectedColorMedia(fallbackMediaId, fallbackRMediaId) {
    const variant = this.#resolveVariant();
    const rMediaView = this.#isRMediaView();
    let mediaId = '';

    if (rMediaView) {
      if (variant?.rMediaId != null) mediaId = String(variant.rMediaId);
      else if (fallbackRMediaId) mediaId = fallbackRMediaId;
    }

    if (!mediaId && variant?.mediaId != null) mediaId = String(variant.mediaId);

    if (!mediaId && this.#data && this.#selectedColor) {
      const { colorOptionPosition } = this.#data;
      if (colorOptionPosition != null) {
        const colorIndex = colorOptionPosition - 1;
        const withMedia = this.#data.variants.find((candidate) => {
          if (candidate.options[colorIndex] !== this.#selectedColor) return false;
          if (rMediaView) return candidate.rMediaId != null || candidate.mediaId != null;
          return candidate.mediaId != null;
        });
        if (withMedia) {
          if (rMediaView && withMedia.rMediaId != null) mediaId = String(withMedia.rMediaId);
          else if (withMedia.mediaId != null) mediaId = String(withMedia.mediaId);
        }
      }
    }

    if (!mediaId && rMediaView && fallbackRMediaId) mediaId = fallbackRMediaId;
    if (!mediaId && fallbackMediaId) mediaId = fallbackMediaId;

    this.#updateGallery(mediaId || undefined);
  }

  /**
   * @returns {boolean}
   */
  #isRMediaView() {
    const grid = this.closest('[product-grid-view]');
    if (grid?.getAttribute('product-grid-view') === 'vista-3') return true;
    if (this.closest('[data-use-r-media="true"]')) return true;
    if (this.closest('.skrey-collection-outfits')) return true;
    return false;
  }

  /**
   * @returns {QuickbuyVariant | null}
   */
  #resolveVariant() {
    if (!this.#data) return null;

    return (
      this.#data.variants.find((variant) =>
        this.#variantMatchesOptions(variant, this.#selectedColor, this.#selectedSize)
      ) || null
    );
  }

  /**
   * @param {QuickbuyVariant} variant
   * @param {string} color
   * @param {string} size
   * @returns {boolean}
   */
  #variantMatchesOptions(variant, color, size) {
    if (!this.#data) return false;

    const { colorOptionPosition, sizeOptionPosition } = this.#data;

    if (colorOptionPosition != null && color) {
      const colorIndex = colorOptionPosition - 1;
      if (variant.options[colorIndex] !== color) return false;
    }

    if (sizeOptionPosition != null && size) {
      const sizeIndex = sizeOptionPosition - 1;
      if (variant.options[sizeIndex] !== size) return false;
    }

    return true;
  }

  /**
   * @param {QuickbuyVariant | null} variant
   */
  #updateMeta(variant) {
    const { colorName, sku, refLabel, meta } = this.refs;
    const skuValue = variant?.sku?.trim() || '';

    if (colorName) {
      colorName.textContent = this.#selectedColor ? this.#selectedColor.toUpperCase() : '';
    }

    if (sku) {
      sku.textContent = skuValue;
    }

    if (refLabel) {
      refLabel.classList.toggle('is-empty', !skuValue);
    }

    const sep = meta?.querySelector('.skrey-quickbuy__meta-sep');
    if (sep instanceof HTMLElement) {
      sep.hidden = !this.#selectedColor || !skuValue;
    }
  }

  /**
   * @returns {HTMLButtonElement | null}
   */
  #getAtcButton() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('.skrey-quickbuy__atc'));
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getAtcLabel() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.skrey-quickbuy__atc-label'));
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    return /** @type {HTMLElement | null} */ (this.querySelector('product-form-component'));
  }

  /**
   * @param {QuickbuyVariant | null} variant
   */
  #updateForm(variant) {
    const form = this.#getProductForm();
    const variantInput = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="id"]'));
    const atcButton = this.#getAtcButton();
    const atcLabel = this.#getAtcLabel();

    const canAdd = Boolean(variant?.available && variant?.id);
    const isBusy = atcButton?.classList.contains('is-loading') || atcButton?.classList.contains('is-added');

    if (variantInput) {
      variantInput.value = variant?.id ? String(variant.id) : '';
      variantInput.disabled = !canAdd;
    }

    if (atcButton && !isBusy) {
      atcButton.disabled = !canAdd;

      if (atcLabel) {
        if (canAdd && atcButton.dataset.addLabel) {
          atcLabel.textContent = atcButton.dataset.addLabel;
        } else if (!canAdd && atcButton.dataset.unavailableLabel) {
          atcLabel.textContent = atcButton.dataset.unavailableLabel;
        }
      }
    }
  }

  /**
   * @param {string | undefined} mediaId
   */
  #updateGallery(mediaId) {
    if (!mediaId) return;

    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (!productCard || typeof productCard.previewVariant !== 'function') return;

    productCard.previewVariant(String(mediaId));
  }

  #bindAddToCartFeedback() {
    const { signal } = this.#abortController;

    // Do not key off form `submit`: Horizon handles `on:submit` in the capture
    // phase and dispatches CartLinesUpdateEvent synchronously inside handleSubmit,
    // before a bubble-phase submit listener can set `is-loading`. That left the
    // spinner stuck because we never attached to the event promise.
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate, { signal });
    document.addEventListener(StandardEvents.cartError, this.#onCartError, { signal });
  }

  /**
   * @param {Event} event
   */
  #onCartLinesUpdate = (event) => {
    const form = this.#getProductForm();
    if (!form || event.target !== form) return;

    /** @type {import('@shopify/events').CartLinesUpdateEvent} */
    const cartEvent = /** @type {import('@shopify/events').CartLinesUpdateEvent} */ (event);

    // Only react to adds from this Quickbuy form (ignore cart drawer updates).
    if (cartEvent.action && cartEvent.action !== 'add') return;

    this.#setLoadingState(true);

    const finish = (/** @type {{ detail?: { didError?: boolean } } | void} */ result) => {
      this.#setLoadingState(false);

      if (result?.detail?.didError) return;
      this.#setAddedState();
    };

    if (cartEvent.promise instanceof Promise) {
      cartEvent.promise.then(finish).catch(() => this.#setLoadingState(false));
      return;
    }

    finish();
  };

  /**
   * @param {Event} event
   */
  #onCartError = (event) => {
    const form = this.#getProductForm();
    if (!form || event.target !== form) return;
    this.#setLoadingState(false);
  };

  /**
   * @param {boolean} isLoading
   */
  #setLoadingState(isLoading) {
    const atcButton = this.#getAtcButton();
    const atcLabel = this.#getAtcLabel();
    if (!atcButton) return;

    atcButton.classList.toggle('is-loading', isLoading);
    atcButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');

    if (isLoading) {
      atcButton.disabled = true;
      if (this.#addedTimeout) {
        clearTimeout(this.#addedTimeout);
        this.#addedTimeout = null;
      }
      atcButton.classList.remove('is-added');
      if (atcLabel && atcButton.dataset.addLabel) {
        atcLabel.textContent = atcButton.dataset.addLabel;
      }
    } else if (!atcButton.classList.contains('is-added')) {
      const variant = this.#resolveVariant();
      const canAdd = Boolean(variant?.available && variant?.id);
      atcButton.disabled = !canAdd;
      if (atcLabel) {
        if (canAdd && atcButton.dataset.addLabel) {
          atcLabel.textContent = atcButton.dataset.addLabel;
        } else if (!canAdd && atcButton.dataset.unavailableLabel) {
          atcLabel.textContent = atcButton.dataset.unavailableLabel;
        }
      }
    }
  }

  #setAddedState() {
    const atcButton = this.#getAtcButton();
    const atcLabel = this.#getAtcLabel();
    if (!atcButton) return;

    atcButton.classList.add('is-added');
    atcButton.disabled = true;

    if (atcLabel && atcButton.dataset.addedLabel) {
      atcLabel.textContent = atcButton.dataset.addedLabel;
    }

    if (this.#addedTimeout) clearTimeout(this.#addedTimeout);

    this.#addedTimeout = setTimeout(() => {
      atcButton.classList.remove('is-added');

      const variant = this.#resolveVariant();
      const canAdd = Boolean(variant?.available && variant?.id);
      atcButton.disabled = !canAdd;

      if (atcLabel) {
        if (canAdd && atcButton.dataset.addLabel) {
          atcLabel.textContent = atcButton.dataset.addLabel;
        } else if (!canAdd && atcButton.dataset.unavailableLabel) {
          atcLabel.textContent = atcButton.dataset.unavailableLabel;
        }
      }

      this.#addedTimeout = null;
    }, 1600);
  }

  #onDialogOpen = () => {
    const trigger = this.#getMobileTrigger();
    trigger?.setAttribute('aria-expanded', 'true');
  };

  #onDialogClose = () => {
    const trigger = this.#getMobileTrigger();
    trigger?.setAttribute('aria-expanded', 'false');
  };

  /**
   * @returns {HTMLElement | null}
   */
  #getMobileTrigger() {
    return this.closest('product-card')?.querySelector('[data-quickbuy-trigger]') ?? null;
  }

  #onBreakpointChange = () => {
    if (isDesktopBreakpoint() && this.refs.dialog.open) {
      this.closeDialog();
    }
  };

  /**
   * Parent resource-list carousels arm slideshow drag on `mousedown`. A slight
   * move then calls setPointerCapture and steals the interaction. Only needed
   * for the desktop on-card sheet (modal dialog is already in the top layer).
   */
  #bindCarouselDragGuard() {
    const { dialog } = this.refs;
    const { signal } = this.#abortController;

    dialog.addEventListener('mousedown', this.#stopCarouselDrag, { signal });
    dialog.addEventListener('pointerdown', this.#stopCarouselDrag, { signal });
  }

  /**
   * @param {Event} event
   */
  #stopCarouselDrag = (event) => {
    if (!isDesktopBreakpoint()) return;
    event.stopPropagation();
  };
}

if (!customElements.get('skrey-quickbuy-component')) {
  customElements.define('skrey-quickbuy-component', SkreyQuickbuyComponent);
}